import { Box, HStack, Image, Link as ChakraLink, Tooltip, usePrefersReducedMotion } from '@chakra-ui/react';
import { Link, useRouter, useRouterState } from '@tanstack/react-router';
import { Plus, UserRound } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useAuthContext } from '@/context/auth/useAuthContext';
import { useAppStore } from '@/store/useAppStore';
import { hasUnacceptedNonContentPolicyDocument } from '@/utils/onboardingUtils';
import { Dashboard, dashboardForPath, getAvailableDashboards } from './dashboardNavigation';
import iconsPng from '@/assets/img/png/icons';

type Tab = { label: string; to: string; icon: string | typeof Plus; whiteIcon?: string };

const tabs: Record<Dashboard, Tab[]> = {
  member: [
    {
      label: 'Dashboard',
      to: '/user/dashboard',
      icon: iconsPng.pillsDashboard,
      whiteIcon: iconsPng.pillsDashboardWhite,
    },
    { label: 'Content Feed', to: '/explore', icon: iconsPng.pillsPlay, whiteIcon: iconsPng.pillsPlayWhite },
    {
      label: 'Growth Zone',
      to: '/user/growth-zone',
      icon: iconsPng.pillsGrowthZone,
      whiteIcon: iconsPng.pillsGrowthZoneWhite,
    },
    {
      label: 'Peer Support',
      to: '/directory/peer-support',
      icon: iconsPng.pillsSupport,
      whiteIcon: iconsPng.pillsSupportWhite,
    },
    {
      label: 'Providers',
      to: '/directory/provider',
      icon: iconsPng.pillsProviders,
      whiteIcon: iconsPng.pillsProvidersWhite,
    },
  ],
  organization: [
    {
      label: 'Dashboard',
      to: '/organization/dashboard',
      icon: iconsPng.pillsDashboard,
      whiteIcon: iconsPng.pillsDashboardWhite,
    },
    { label: 'Roster', to: '/organization/roster', icon: iconsPng.pillsRoster, whiteIcon: iconsPng.pillsRosterWhite },
  ],
  peerSupport: [
    {
      label: 'Dashboard',
      to: '/organization/dashboard/peer-support',
      icon: iconsPng.pillsDashboard,
      whiteIcon: iconsPng.pillsDashboardWhite,
    },
    {
      label: 'Peer Support Team',
      to: '/directory/peer-support',
      icon: iconsPng.pillsSupport,
      whiteIcon: iconsPng.pillsSupportWhite,
    },
  ],
  creator: [
    {
      label: 'Dashboard',
      to: '/creator/dashboard',
      icon: iconsPng.pillsDashboard,
      whiteIcon: iconsPng.pillsDashboardWhite,
    },
    { label: 'Content Feed', to: '/explore', icon: iconsPng.pillsPlay, whiteIcon: iconsPng.pillsPlayWhite },
    { label: 'My Library', to: '/creator/library', icon: iconsPng.pillsLibrary, whiteIcon: iconsPng.pillsLibraryWhite },
    {
      label: 'Creator Profile',
      to: '/creator/profile',
      icon: UserRound,
    },
    { label: 'Create Content', to: '/creator/content/create', icon: Plus },
  ],
} as const;

const DashboardTabs = () => {
  const router = useRouter();
  const [pendingTab, setPendingTab] = useState<string | null>(null);

  const navigationId = useRef(0);

  const prefersReducedMotion = usePrefersReducedMotion();

  const { user, isSuperAdmin } = useAuthContext();

  const { activeDashboard, setActiveDashboard, legalAcceptanceStatuses, hasUnacceptedLegalDocuments } = useAppStore();

  const pathname = useRouterState({ select: (state) => (state.resolvedLocation ?? state.location).pathname });
  const available = getAvailableDashboards(user?.roles);
  const routeDashboard = dashboardForPath(pathname);
  const dashboard: Dashboard =
    (routeDashboard && available.includes(routeDashboard) ? routeDashboard : null) ??
    (activeDashboard && available.includes(activeDashboard) ? activeDashboard : null) ??
    available[0] ??
    'member';

  useEffect(() => {
    if (dashboard !== activeDashboard && available.length > 0) setActiveDashboard(dashboard);
  }, [dashboard, activeDashboard, setActiveDashboard, available.length]);

  useEffect(() => {
    for (const tab of tabs[dashboard]) {
      if (tab.whiteIcon) new window.Image().src = tab.whiteIcon;
    }
  }, [dashboard]);

  if (
    isSuperAdmin ||
    !user ||
    pathname.includes('onboarding') ||
    pathname.includes('terms') ||
    pathname.includes('privacy') ||
    pathname.startsWith('/eula/') ||
    hasUnacceptedLegalDocuments ||
    hasUnacceptedNonContentPolicyDocument(legalAcceptanceStatuses)
  )
    return null;

  const selected = (to: string, currentPath: string) =>
    to === '/explore'
      ? currentPath.startsWith('/explore') ||
        currentPath.startsWith('/article/') ||
        currentPath.startsWith('/content/') ||
        currentPath.startsWith('/loop/')
      : currentPath === to || currentPath.startsWith(`${to}/`);

  const activeIndex = tabs[dashboard].findIndex(({ to }) => selected(to, pendingTab ?? pathname));

  return (
    <HStack
      position="absolute"
      left="50%"
      transform="translateX(-50%)"
      gap="4px"
      padding="4px"
      height="44px"
      borderRadius="full"
      bg="white"
      boxShadow="0px 4px 16px rgba(0, 65, 121, 0.15)"
      role="navigation"
      aria-label={`${dashboard} dashboard tabs`}
    >
      <Box
        position="absolute"
        top="4px"
        left="4px"
        width="72px"
        height="36px"
        borderRadius="full"
        bg="primary.500"
        pointerEvents="none"
        opacity={activeIndex < 0 ? 0 : 1}
        transform={`translateX(${Math.max(activeIndex, 0) * 76}px)`}
        transition={prefersReducedMotion ? 'none' : 'transform 340ms ease, opacity 160ms ease'}
      />
      {tabs[dashboard].map(({ label, to, icon, whiteIcon }, index) => {
        const active = index === activeIndex;
        const Icon = typeof icon === 'string' ? null : icon;
        return (
          <Tooltip key={label} label={label} placement="bottom" hasArrow>
            <ChakraLink
              as={Link}
              to={to}
              onClick={(event) => {
                if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                if (pathname === to && !pendingTab) return;
                event.preventDefault();
                const id = ++navigationId.current;
                flushSync(() => setPendingTab(to));
                void router.navigate({ to }).finally(() => {
                  if (navigationId.current === id) setPendingTab(null);
                });
              }}
              aria-label={label}
              aria-current={active ? 'page' : undefined}
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="72px"
              height="36px"
              borderRadius="full"
              bg="transparent"
              color={active ? 'white' : 'text.darkBlue'}
              position="relative"
              zIndex={1}
              _hover={{ bg: active ? 'transparent' : 'background.lightBlue' }}
            >
              {Icon ? (
                <Icon size={24} strokeWidth={2.5} />
              ) : (
                <Image
                  src={active ? whiteIcon : typeof icon === 'string' ? icon : undefined}
                  alt=""
                  width="24px"
                  height="24px"
                  objectFit="contain"
                />
              )}
            </ChakraLink>
          </Tooltip>
        );
      })}
    </HStack>
  );
};

export default DashboardTabs;
